import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import AdvisorsStats from "@/pages/admin/asesores/components/AdvisorsStats";
import AdvisorTable from "@/pages/admin/asesores/components/AdvisorTable";
import AdvisorMasterModal from "@/pages/admin/asesores/components/AdvisorMasterModal";
import PendingApprovals from "@/pages/admin/asesores/components/PendingApprovals";
import { buildAdvisorAccounts } from "@/utils/accounting";
import type { ApprovalStatus, Distributor } from "@/types";

const AdvisorsPage = () => {
  const { data, updateDistributor, setApprovalStatus, deleteDistributor, refresh } = useApp();
  const [editing, setEditing] = useState<Distributor | null>(null);
  const [feedback, setFeedback] = useState("");
  const [busyId, setBusyId] = useState<string | undefined>(undefined);

  const accounts = useMemo(() => buildAdvisorAccounts(data), [data]);

  const approved = useMemo(
    () => data.distributors.filter((d) => d.approvalStatus === "approved"),
    [data.distributors]
  );

  const editingAccount = useMemo(
    () => (editing ? accounts.find((a) => a.advisor.id === editing.id) ?? null : null),
    [editing, accounts]
  );

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(""), 2600);
  };

  const STATUS_MESSAGE: Record<ApprovalStatus, string> = {
    approved: "Cuenta aprobada. El asesor ya puede entrar a su portal.",
    rejected: "Solicitud rechazada.",
    pending: "La cuenta quedó pendiente de aprobación.",
    suspended: "Acceso suspendido. El asesor no podrá entrar mientras esté suspendido.",
  };

  const handleSetStatus = async (id: string, status: ApprovalStatus) => {
    setBusyId(id);
    try {
      await setApprovalStatus(id, status);
      await refresh();
      notify(STATUS_MESSAGE[status]);
      // Al aprobar, abrimos la ficha máster de una vez para asignar margen y cupo.
      if (status === "approved") {
        const current = data.distributors.find((d) => d.id === id);
        if (current) setEditing({ ...current, approvalStatus: "approved" });
      }
    } finally {
      setBusyId(undefined);
    }
  };

  const handleSave = async (d: Distributor) => {
    await updateDistributor(d);
    setEditing(null);
    notify("Ficha del asesor actualizada.");
  };

  // Suspender/reactivar desde la ficha máster: cerramos la ficha y no la reabrimos.
  const handleStatusFromModal = async (id: string, status: ApprovalStatus) => {
    setEditing(null);
    setBusyId(id);
    try {
      await setApprovalStatus(id, status);
      await refresh();
      notify(STATUS_MESSAGE[status]);
    } finally {
      setBusyId(undefined);
    }
  };

  // Eliminar por completo la cuenta del asesor (solo si está a paz y salvo).
  const handleDelete = async (id: string) => {
    setEditing(null);
    setBusyId(id);
    try {
      await deleteDistributor(id);
      await refresh();
      notify("La cuenta del asesor fue eliminada.");
    } finally {
      setBusyId(undefined);
    }
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Red de Asesores y Verificación"
        subtitle="Aprueba, rechaza o suspende cuentas y administra la ficha máster de cada distribuidor: identidad, condiciones comerciales, verificación de cédula y tenencia en consignación."
      />

      <AdvisorsStats distributors={approved} inventory={data.inventory} products={data.products} />

      <div className="mt-5 mb-5">
        <PendingApprovals
          distributors={data.distributors}
          onSetStatus={handleSetStatus}
          onOpenFicha={setEditing}
          busyId={busyId}
        />
      </div>

      {feedback && (
        <div className="mb-3 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      <div className="mb-3">
        <h3 className="editable-title text-base font-bold">Padrón activo en consignación</h3>
        <p className="text-[11px] text-foreground-600">
          Asesores aprobados y su tenencia actual. Abre la ficha para ajustar condiciones o
          suspender el acceso.
        </p>
      </div>

      <AdvisorTable
        distributors={approved}
        inventory={data.inventory}
        products={data.products}
        onEdit={setEditing}
      />

      <AdvisorMasterModal
        open={editing !== null}
        advisor={editing}
        account={editingAccount}
        onClose={() => setEditing(null)}
        onSave={handleSave}
        onSetStatus={handleStatusFromModal}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default AdvisorsPage;